import moment from 'moment';
import { saveAs } from 'file-saver';
import { formatter } from '../../../constants';

export const REPORT_TYPES = {
  SUMMARY: 'summary',
  INVOICE: 'invoice',
  DETAIL: 'detail',
};

export const DATE_PERIODS = {
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  THIS_QUARTER: 'this_quarter',
  LAST_QUARTER: 'last_quarter',
  THIS_YEAR: 'this_year',
  LAST_YEAR: 'last_year',
  ALL_DATES: 'all',
  CUSTOM: 'custom',
};

export const parseMoney = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  return Number(String(value).replace(/[^0-9.-]+/g, '')) || 0;
};

export const invoiceAmount = (invoice) => parseMoney(invoice?.invoice_total_amount);

export const invoiceHauling = (invoice) => parseMoney(invoice?.unit_package_value);

export const invoiceWait = (invoice) => parseMoney(invoice?.waittime_package_value);

export const invoiceMaterial = (invoice) => parseMoney(invoice?.material_package_vale);

export const invoiceMaterialTax = (invoice) => parseMoney(invoice?.material_tax);

export const invoiceMaterialWithTax = (invoice) => invoiceMaterial(invoice) + invoiceMaterialTax(invoice);

export const invoiceDateValue = (invoice) => invoice?.invoice_date || invoice?.date_created;

export const formatInvoiceDate = (invoice, pattern = 'MM/DD/YY') => {
  const value = invoiceDateValue(invoice);
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(pattern) : '';
};

export const customerName = (customer) =>
  customer?.customer_info?.user_fname || customer?.user_fname || customer?.name || '';

export const instanceCustomerName = (instance) =>
  instance?.customer?.user_fname || instance?.invoice?.customer_name || '';

export const jobLabel = (jobInfo, companyId) => {
  if (!jobInfo) return '';
  const number = jobInfo.job_no != null ? `${companyId ? `${companyId}-` : ''}${jobInfo.job_no}` : '';
  const location = jobInfo.l_name || '';
  const name = jobInfo.name || '';
  const unit = jobInfo.unit_name || '';
  return [number, location, name, unit].filter(Boolean).join(' - ');
};

export const sortCustomers = (customers = {}) =>
  Object.keys(customers).sort((a, b) =>
    customerName(customers[a]).localeCompare(customerName(customers[b]), undefined, { sensitivity: 'base' })
  );

export const sortInvoices = (invoices = []) =>
  [...invoices].sort((a, b) => {
    const dateA = moment(invoiceDateValue(a?.invoice));
    const dateB = moment(invoiceDateValue(b?.invoice));
    if (dateA.isValid() && dateB.isValid() && !dateA.isSame(dateB)) {
      return dateA.valueOf() - dateB.valueOf();
    }
    return String(a?.invoice?.invoice_no || '').localeCompare(String(b?.invoice?.invoice_no || ''), undefined, {
      numeric: true,
    });
  });

export const getDatePeriodRange = (period) => {
  const now = moment();
  switch (period) {
    case DATE_PERIODS.THIS_MONTH:
      return {
        start: now.clone().startOf('month').format('YYYY-MM-DD'),
        end: now.clone().endOf('month').format('YYYY-MM-DD'),
      };
    case DATE_PERIODS.LAST_MONTH:
      return {
        start: now.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        end: now.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case DATE_PERIODS.THIS_QUARTER:
      return {
        start: now.clone().startOf('quarter').format('YYYY-MM-DD'),
        end: now.clone().endOf('quarter').format('YYYY-MM-DD'),
      };
    case DATE_PERIODS.LAST_QUARTER:
      return {
        start: now.clone().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD'),
        end: now.clone().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD'),
      };
    case DATE_PERIODS.THIS_YEAR:
      return {
        start: now.clone().startOf('year').format('YYYY-MM-DD'),
        end: now.clone().endOf('year').format('YYYY-MM-DD'),
      };
    case DATE_PERIODS.LAST_YEAR:
      return {
        start: now.clone().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        end: now.clone().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
      };
    case DATE_PERIODS.ALL_DATES:
      return { start: '', end: '' };
    default:
      return null;
  }
};

export const getReportTypeTitleKey = (reportType) => {
  if (reportType === REPORT_TYPES.SUMMARY) return 'SALES_BY_CUSTOMER';
  if (reportType === REPORT_TYPES.INVOICE) return 'SALES_BY_CUSTOMER_INVOICE';
  return 'SALES_BY_CUSTOMER_DETAIL';
};

export const shouldShowInvoiceLines = (reportType, groupByCustomer) => {
  if (reportType === REPORT_TYPES.SUMMARY && groupByCustomer) return false;
  return true;
};

const matchesInvoiceSearch = (instance, query) => {
  if (!query) return true;
  const invoiceNo = String(instance?.invoice?.invoice_no ?? '').toLowerCase();
  const jobNo = String(instance?.invoice?.job_no ?? '').toLowerCase();
  const name = instanceCustomerName(instance).toLowerCase();
  return invoiceNo.includes(query) || jobNo.includes(query) || name.includes(query);
};

const emptyTotals = () => ({
  invoice_count: 0,
  total_hauling: 0,
  total_material: 0,
  total_material_tax: 0,
  total_waittime: 0,
  total_agent_commission: 0,
  total_invoice_amount: 0,
});

const addInstanceTotals = (totals, instance) => {
  totals.invoice_count += 1;
  totals.total_hauling += invoiceHauling(instance.invoice);
  totals.total_material += invoiceMaterial(instance.invoice);
  totals.total_material_tax += invoiceMaterialTax(instance.invoice);
  totals.total_waittime += invoiceWait(instance.invoice);
  totals.total_agent_commission += Number(instance.agent_comm) || 0;
  totals.total_invoice_amount += invoiceAmount(instance.invoice);
  return totals;
};

export const filterReportData = (reportData, searchText) => {
  if (!reportData) return reportData;
  const query = String(searchText || '').trim().toLowerCase();
  if (!query) return reportData;

  const invoices = sortInvoices((reportData.invoices || []).filter((instance) => matchesInvoiceSearch(instance, query)));
  const customers = {};

  Object.keys(reportData.customers || {}).forEach((customerId) => {
    const customer = reportData.customers[customerId];
    const customerInvoices = sortInvoices(
      (customer.invoices || []).filter((instance) => matchesInvoiceSearch(instance, query))
    );
    if (!customerInvoices.length) return;

    const jobs = {};
    Object.keys(customer.jobs || {}).forEach((jobId) => {
      const job = customer.jobs[jobId];
      const jobInvoices = sortInvoices((job.invoices || []).filter((instance) => matchesInvoiceSearch(instance, query)));
      if (!jobInvoices.length) return;
      jobs[jobId] = {
        ...job,
        invoices: jobInvoices,
        ...jobInvoices.reduce((totals, instance) => addInstanceTotals(totals, instance), emptyTotals()),
      };
    });

    customers[customerId] = {
      ...customer,
      invoices: customerInvoices,
      jobs,
      ...customerInvoices.reduce((totals, instance) => addInstanceTotals(totals, instance), emptyTotals()),
    };
  });

  const grand = invoices.reduce((totals, instance) => addInstanceTotals(totals, instance), emptyTotals());

  return {
    ...reportData,
    customers,
    invoices,
    total_invoice_count: grand.invoice_count,
    total_hauling: grand.total_hauling,
    total_material: grand.total_material,
    total_material_tax: grand.total_material_tax,
    total_waittime: grand.total_waittime,
    total_agent_commission: grand.total_agent_commission,
    total_invoice_amount: grand.total_invoice_amount,
  };
};

const escapeCsv = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const moneyCsv = (value) => parseMoney(value).toFixed(2);

export const buildSalesReportCsv = ({
  reportData,
  reportType,
  groupByCustomer,
  groupByJob,
  companyId,
  showCommission,
  t,
}) => {
  const showLines = shouldShowInvoiceLines(reportType, groupByCustomer);
  const compact = reportType === REPORT_TYPES.INVOICE;
  const headers = compact
    ? [
        t('DATE'),
        t('CUSTOMER'),
        t('INVOICE_NO'),
        t('JOB_NO'),
        t('STATUS'),
        t('GRAND_TOTAL'),
      ]
    : showLines
    ? [
        t('DATE'),
        t('CUSTOMER'),
        t('INVOICE_NO'),
        t('JOB_NO'),
        t('HAULING'),
        t('WAIT_TIME'),
        t('MATERIAL_W_TAX'),
        t('STATUS'),
        ...(showCommission ? [t('AGENT_COMMISSION')] : []),
        t('GRAND_TOTAL'),
      ]
    : [
        t('CUSTOMER'),
        t('INVOICE_COUNT'),
        t('HAULING'),
        t('WAIT_TIME'),
        t('MATERIAL_W_TAX'),
        ...(showCommission ? [t('AGENT_COMMISSION')] : []),
        t('GRAND_TOTAL'),
      ];

  const rows = [headers];

  const pushInvoiceRow = (instance, nameOverride) => {
    const invoice = instance.invoice || {};
    const name = nameOverride || instanceCustomerName(instance);
    if (compact) {
      rows.push([
        formatInvoiceDate(invoice, 'MM/DD/YYYY'),
        name,
        invoice.invoice_no,
        companyId ? `${companyId}-${invoice.job_no}` : invoice.job_no,
        invoice.status,
        moneyCsv(invoice.invoice_total_amount),
      ]);
      return;
    }
    rows.push([
      formatInvoiceDate(invoice, 'MM/DD/YYYY'),
      name,
      invoice.invoice_no,
      companyId ? `${companyId}-${invoice.job_no}` : invoice.job_no,
      moneyCsv(invoice.unit_package_value),
      moneyCsv(invoice.waittime_package_value),
      moneyCsv(invoiceMaterialWithTax(invoice)),
      invoice.status,
      ...(showCommission ? [moneyCsv(instance.agent_comm)] : []),
      moneyCsv(invoice.invoice_total_amount),
    ]);
  };

  const pushTotalRow = (label, totals) => {
    if (compact) {
      rows.push(['', label, '', '', '', moneyCsv(totals.total_invoice_amount)]);
      return;
    }
    if (!showLines) {
      rows.push([
        label,
        totals.invoice_count,
        moneyCsv(totals.total_hauling),
        moneyCsv(totals.total_waittime),
        moneyCsv(totals.total_material + totals.total_material_tax),
        ...(showCommission ? [moneyCsv(totals.total_agent_commission)] : []),
        moneyCsv(totals.total_invoice_amount),
      ]);
      return;
    }
    rows.push([
      '',
      label,
      '',
      '',
      moneyCsv(totals.total_hauling),
      moneyCsv(totals.total_waittime),
      moneyCsv(totals.total_material + totals.total_material_tax),
      '',
      ...(showCommission ? [moneyCsv(totals.total_agent_commission)] : []),
      moneyCsv(totals.total_invoice_amount),
    ]);
  };

  if (showLines && !groupByCustomer && !groupByJob) {
    sortInvoices(reportData.invoices || []).forEach((instance) => pushInvoiceRow(instance));
  } else {
    sortCustomers(reportData.customers || {}).forEach((customerId) => {
      const customer = reportData.customers[customerId];
      const name = customerName(customer);

      if (!showLines) {
        if (groupByJob) {
          Object.keys(customer.jobs || {}).forEach((jobId) => {
            const job = customer.jobs[jobId];
            pushTotalRow(`${name} / ${jobLabel(job.job_info, companyId)}`, job);
          });
        } else {
          pushTotalRow(name, customer);
        }
        return;
      }

      if (groupByJob) {
        Object.keys(customer.jobs || {}).forEach((jobId) => {
          const job = customer.jobs[jobId];
          sortInvoices(job.invoices || []).forEach((instance) =>
            pushInvoiceRow(instance, groupByCustomer ? name : instanceCustomerName(instance))
          );
          pushTotalRow(`${t('JOB')} ${jobLabel(job.job_info, companyId)}`, job);
        });
      } else {
        sortInvoices(customer.invoices || []).forEach((instance) => pushInvoiceRow(instance, name));
      }

      if (groupByCustomer) {
        pushTotalRow(`${t('CUSTOMER_TOTAL')} ${name}`, customer);
      }
    });
  }

  pushTotalRow(t('GRAND_TOTAL'), {
    invoice_count: reportData.total_invoice_count,
    total_hauling: reportData.total_hauling,
    total_material: reportData.total_material,
    total_material_tax: reportData.total_material_tax,
    total_waittime: reportData.total_waittime,
    total_agent_commission: reportData.total_agent_commission,
    total_invoice_amount: reportData.total_invoice_amount,
  });

  return rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
};

export const exportSalesReportCsv = ({
  reportData,
  reportType,
  groupByCustomer,
  groupByJob,
  companyId,
  showCommission,
  startDate,
  endDate,
  t,
}) => {
  const csv = buildSalesReportCsv({
    reportData,
    reportType,
    groupByCustomer,
    groupByJob,
    companyId,
    showCommission,
    t,
  });
  const titleKey = getReportTypeTitleKey(reportType);
  const range =
    startDate && endDate
      ? `${moment(startDate).format('YYYY-MM-DD')}_to_${moment(endDate).format('YYYY-MM-DD')}`
      : 'all';
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${t(titleKey).replace(/\s+/g, '_')}_${range}.csv`);
};

export const formatMoney = (value) => formatter.format(parseMoney(value));
